import type { Store } from 'redux';
import * as React from 'react';
import { useLayoutEffect } from 'react';
import { Provider } from 'react-redux';
import * as ReactDOM from 'react-dom';
import invariant from 'invariant';

import '@/types/dom';

const getFiberNode = (domNode: Element) => {
  // https://stackoverflow.com/a/39165137/8207842
  const key = Object.keys(domNode).find((p) => p.startsWith('__reactInternalInstance$'));
  return domNode[key];
};

const collectContexts = (fiberNode: any) => {
  let node = fiberNode;
  const contexts: Array<[React.Context<any>, any]> = [];
  while (node.return) {
    node = node.return;
    if (node.type?.$$typeof === Symbol.for('react.provider')) {
      const { value } = node.memoizedProps;
      const Context = node.type._context; // eslint-disable-line no-underscore-dangle
      contexts.push([Context, value]);
    }
  }
  return contexts;
};

interface MeasurementsContainerProps {
  children: React.ReactChild[];
  onMount: () => void;
  reduxStore?: Store; // eslint-disable-line react/require-default-props
  contexts: Array<[React.Context<any>, any]>;
}

export const MeasurementsContainerContext = React.createContext(false);

const MeasurementsContainer = (
  { children, onMount, reduxStore, contexts }: MeasurementsContainerProps,
) => {
  useLayoutEffect(onMount, []);
  let element: React.ReactElement = React.createElement('div', {}, ...children);
  if (reduxStore) element = React.createElement(Provider, { store: reduxStore }, element);
  element = React.createElement(MeasurementsContainerContext.Provider, { value: true }, element);
  element = contexts.reduce(
    (acc, [Context, value]) => React.createElement(Context.Provider, { value }, acc),
    element,
  );
  return element;
};

export const measureReactElements =
  async (domNode: HTMLElement, elements: React.ReactChild[], reduxStore?: Store) => {
    if (elements.length < 1) return [];

    const container = domNode.closest('.measurements-container');
    invariant(!!container, 'Could not find measurements container');
    const canvas = container.querySelector('.measurements-canvas');
    invariant(!!canvas, 'Could not find measurements canvas');

    const fiberNode = getFiberNode(canvas);
    const contexts = collectContexts(fiberNode);

    let resolvePromise: () => void;
    const promise = new Promise((resolve: any) => {
      resolvePromise = resolve;
    });

    // eslint-disable-next-line react/no-children-prop
    const rootElement = React.createElement(
      MeasurementsContainer,
      { onMount: resolvePromise, reduxStore, contexts, children: elements },
    );

    ReactDOM.render(rootElement, canvas); // eslint-disable-line

    await promise;
    const result: number[] = [];

    for (const item of canvas.children[0].children) {
      const { height } = item.getBoundingClientRect();
      result.push(height);
    }
    ReactDOM.unmountComponentAtNode(canvas);

    return result;
  };
